import { LineProbeRunner, LineProbeStatsQueue, type RunnerOptions, type SessionGrant } from '@core/line-probe';
import type { ApiResponse, BaseApiResponse } from '@core/swagger-client';
import { LiveStatus, WebinarStatus } from '@core/types';
import type { LineProbeHub } from '@online-webinar/store';

/** Spec 9 cadence of a broadcast: a probe per target every 50 ms. */
export const BROADCAST_INTERVAL_MS = 50;
/** After a 429 the hub is asked again no sooner than this. */
export const THROTTLED_RETRY_MS = 10 * 60_000;
/** After any other failed first grant (the runner itself retries a failed renewal after a minute). */
export const GRANT_RETRY_MS = 60_000;
/**
 * A running probe stops for "not the active presenter" only once that has held this long. A hub
 * reconnect can hand the broadcaster a new session before the hub repeats who presents, so the
 * role blinks off and on; restarting each time would spend one of the ten grants an hour.
 */
export const PRESENTER_GRACE_MS = 5_000;

/** What the store says about this broadcaster, reduced to what the line probe decides on. */
export interface LineProbeState {
    /** Joined this webinar: `leaveWebinar` (and a new join) resets `me` to null. */
    joined: boolean;
    isActivePresenter: boolean;
    hubConnected: boolean;
    webinarEnded: boolean;
}

const FINISHED = new Set<WebinarStatus | undefined>([WebinarStatus.Ended, WebinarStatus.Cancelled, WebinarStatus.Expired]);

/** The part of the store's state the line probe reads; the store's `RootState` fits it. */
export interface LineProbeStoreState {
    liveWebinar: {
        me: unknown | null;
        isActivePresenter: boolean | null;
        dataConnection: { status: LiveStatus };
        webinarData?: { status?: WebinarStatus };
    };
}

/**
 * Maps the store onto the line probe's decision. Before the join `me` and `presenter` are both null
 * and the slice calls that a match, so the active presenter also needs `me`.
 */
export function selectLineProbeState(root: LineProbeStoreState): LineProbeState {
    const live = root.liveWebinar;
    const joined = live.me != null;
    return {
        joined,
        isActivePresenter: joined && live.isActivePresenter === true,
        hubConnected: live.dataConnection.status === LiveStatus.Live,
        webinarEnded: FINISHED.has(live.webinarData?.status)
    };
}

/**
 * Whether the line probe should be measuring (spec 8): only for the active presenter of a webinar
 * that has not ended. The hub only matters for the start, since the grant comes through it; a hub
 * outage does not stop a running probe, its windows wait in the queue. The controller lets a lost
 * presenter role last {@link PRESENTER_GRACE_MS} before it acts on it.
 */
export function lineProbeShouldRun(state: LineProbeState, running: boolean): boolean {
    return state.joined && state.isActivePresenter && !state.webinarEnded && (running || state.hubConnected);
}

/** Leaving the webinar and its end stop the probe at once, without the presenter grace. */
export function lineProbeStopsAtOnce(state: LineProbeState): boolean {
    return !state.joined || state.webinarEnded;
}

/**
 * - `disabled`: the line probe or its broadcast use is off for this tenant (409 `LineProbeDisabled`),
 *   or the grant has no targets. Nothing to retry for this page.
 * - `throttled`: ten grants this hour already (429 `ThrottledException`). Ask again after ten minutes.
 * - `retry`: anything else, including a dropped hub call. Ask again after a minute.
 */
export type GrantFailure = 'disabled' | 'throttled' | 'retry';

export function classifyGrantFailure(response: BaseApiResponse): GrantFailure {
    if (response.errorCode === 'LineProbeDisabled') return 'disabled';
    if (response.statusCode === 429 || response.errorCode === 'ThrottledException') return 'throttled';
    return 'retry';
}

/** The parts of the runner the controller uses, so tests can hand in a fake. */
export interface LineProbeRunnerLike {
    readonly sessionId: string | undefined;
    start(): Promise<void>;
    stop(): void;
}

export interface LineProbeControllerOptions {
    hub: LineProbeHub;
    warn: (message: string, parameters?: object) => void;
    createRunner?: (options: RunnerOptions) => LineProbeRunnerLike;
    now?: () => number;
    setTimeout?: (fn: () => void, ms: number) => unknown;
    clearTimeout?: (handle: unknown) => void;
}

/** Thrown by getGrant after the controller has acted on the failure, so start() can tell it apart. */
class GrantRefused extends Error {
    constructor(readonly failure: GrantFailure) {
        super(`Line probe grant refused: ${failure}`);
    }
}

const NO_TARGETS: BaseApiResponse = { success: false, statusCode: 409, errorCode: 'LineProbeDisabled' };

/**
 * Owns the broadcaster's line probe: one runner at a time, started and stopped by `update` as the
 * store changes, with its windows queued for the hub. Never throws and shows nothing; failures end
 * up in the log at most.
 */
export class LineProbeController {
    private readonly createRunner: (options: RunnerOptions) => LineProbeRunnerLike;
    private readonly now: () => number;
    private readonly setTimeout: (fn: () => void, ms: number) => unknown;
    private readonly clearTimeout: (handle: unknown) => void;

    private state: LineProbeState = { joined: false, isActivePresenter: false, hubConnected: false, webinarEnded: false };
    private runner?: LineProbeRunnerLike;
    private unsubscribe?: () => void;
    /** Set by a `LineProbeDisabled` answer: nothing more for the lifetime of this controller (the page). */
    private disabled = false;
    private disposed = false;
    private nextStartAt = 0;
    private retryTimer?: unknown;
    private graceTimer?: unknown;

    constructor(private readonly options: LineProbeControllerOptions) {
        this.createRunner = options.createRunner ?? ((o) => new LineProbeRunner(o));
        this.now = options.now ?? Date.now;
        this.setTimeout = options.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
        this.clearTimeout = options.clearTimeout ?? ((handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>));
    }

    get running(): boolean {
        return this.runner !== undefined;
    }

    update(state: LineProbeState): void {
        this.state = state;
        if (this.disposed) return;
        this.safely('Line probe could not be started or stopped', () => {
            if (lineProbeStopsAtOnce(state)) {
                this.stopRunner();
                this.cancelRetry();
                return;
            }
            if (this.running) {
                // Only the presenter role can stop a running probe here, and only once it is gone for good.
                if (lineProbeShouldRun(state, true)) this.cancelGrace();
                else this.armGrace();
                return;
            }
            if (!lineProbeShouldRun(state, false)) {
                this.cancelRetry();
                return;
            }
            if (this.disabled) return;
            const wait = this.nextStartAt - this.now();
            if (wait > 0) {
                if (this.retryTimer === undefined) {
                    this.retryTimer = this.setTimeout(() => {
                        this.retryTimer = undefined;
                        this.update(this.state);
                    }, wait);
                }
                return;
            }
            this.startRunner();
        });
    }

    /** Unmount: stops the runner; its last windows still get one send. Never throws into React. */
    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.safely('Line probe could not be stopped', () => {
            this.cancelRetry();
            this.stopRunner();
            this.unsubscribe?.();
            this.unsubscribe = undefined;
        });
    }

    private safely(message: string, action: () => void): void {
        try {
            action();
        } catch (e) {
            this.options.warn(message, { error: String(e) });
        }
    }

    private armGrace(): void {
        if (this.graceTimer !== undefined) return;
        this.graceTimer = this.setTimeout(() => {
            this.graceTimer = undefined;
            if (this.disposed || lineProbeShouldRun(this.state, true)) return;
            this.safely('Line probe could not be stopped', () => {
                this.stopRunner();
                this.cancelRetry();
            });
        }, PRESENTER_GRACE_MS);
    }

    private cancelGrace(): void {
        if (this.graceTimer === undefined) return;
        this.clearTimeout(this.graceTimer);
        this.graceTimer = undefined;
    }

    private startRunner(): void {
        // A queue of an earlier run keeps its reconnect flush until a new run replaces it.
        this.unsubscribe?.();

        let runner: LineProbeRunnerLike | undefined = undefined;
        const queue = new LineProbeStatsQueue(
            async (batch) => {
                try {
                    return await this.options.hub.stats(batch);
                } catch {
                    // A dropped hub call: keep the windows for the next flush
                    return { success: false, statusCode: 500 };
                }
            },
            () => this.options.hub.isConnected(),
            () => runner?.sessionId
        );
        runner = this.createRunner({
            intervalMs: BROADCAST_INTERVAL_MS,
            getGrant: () => this.grant(runner!),
            onSummary: (window) => queue.push(window),
            warn: this.options.warn
        });
        const started = runner;
        this.runner = started;
        this.unsubscribe = this.options.hub.onConnected(() => void queue.flush());

        started.start().catch((e: unknown) => {
            if (e instanceof GrantRefused) return;
            if (this.runner !== started) return;
            this.options.warn('Line probe could not be started', { error: String(e) });
            this.stopRunner();
            this.retryAfter(GRANT_RETRY_MS);
        });
    }

    /**
     * The runner's getGrant, for the first grant and every renewal. A refusal is acted on here,
     * before the runner sees it: disabled and throttled stop the runner at once (so it neither
     * warns nor retries itself), a first grant that failed otherwise is retried after a minute, a
     * renewal that failed otherwise is left to the runner's own one-minute retry.
     */
    private async grant(runner: LineProbeRunnerLike): Promise<SessionGrant> {
        let response: ApiResponse<SessionGrant>;
        try {
            response = await this.options.hub.session();
        } catch (e) {
            response = { success: false, statusCode: 500, message: String(e) };
        }
        if (response.success && response.data && response.data.targets.length > 0) return response.data;

        const failure = classifyGrantFailure(response.success ? NO_TARGETS : response);
        const current = this.runner === runner;
        const firstGrant = runner.sessionId === undefined;
        // A renewal that failed for a passing reason: the runner keeps measuring and asks again.
        if (failure === 'retry' && current && !firstGrant) throw response;

        // Disabled and throttled are facts about the tenant and this participant, even for a runner
        // that was stopped while it waited for the answer.
        if (failure === 'disabled') this.disabled = true;
        if (current) {
            if (failure === 'retry') {
                this.options.warn('Line probe grant failed', { statusCode: response.statusCode, errorCode: response.errorCode });
            }
            this.stopRunner();
        }
        if (failure === 'throttled') this.retryAfter(THROTTLED_RETRY_MS);
        else if (failure === 'retry' && current) this.retryAfter(GRANT_RETRY_MS);
        throw new GrantRefused(failure);
    }

    private stopRunner(): void {
        this.cancelGrace();
        const runner = this.runner;
        if (!runner) return;
        this.runner = undefined;
        // Stopping hands the last, partial windows to the queue, which sends them when it can.
        runner.stop();
    }

    private retryAfter(ms: number): void {
        this.nextStartAt = this.now() + ms;
        this.cancelRetry();
        this.update(this.state);
    }

    private cancelRetry(): void {
        if (this.retryTimer === undefined) return;
        this.clearTimeout(this.retryTimer);
        this.retryTimer = undefined;
    }
}
